import { BaseListStore } from './baseListStore';
import { AbstractBaseListStore } from './abstractBaseListStore';
import { EFaceDataBaseType, IFaceData, IFaceDataBase, IFaceDataExtra } from '@/editor/data/face/face';
import { EEntityType } from '@/editor/types';
import { TGlobalStores } from '..';
import { IMaterialInfo } from '@/editor/data/material/material';

export class BaseFaceStore implements AbstractBaseListStore<IFaceData> {
  private _faceBaseDataMap;
  private _faceExtraDataMap;

  constructor(gs: TGlobalStores) {
    this._faceBaseDataMap = new BaseListStore<IFaceDataBase>(gs, {
      dataType: EEntityType.Face,
      onDataChanged: (changedId) => this._syncExtraData(changedId)
    });
    this._faceExtraDataMap = new Map<string, IFaceDataExtra>();
  }

  /**
   * 让 extra 表跟着 base 表同生同死，理由同 BaseRoomStore._syncExtraData。
   *
   * 占位 extra 不写 relativeInfo —— 缺这个 key 时 get() 拼出来的就是 base 里创建时那份，
   * 正是 refreshWallFaceDatas 认领(getWallFaceRelativeMap)要用的东西；几何交给随后的 updateExtra 覆盖。
   */
  private _syncExtraData(changedId?: string) {
    // clear / addAll 是整表替换，不带 id，按全量对齐
    if (changedId === undefined) {
      const baseIds = new Set(this._faceBaseDataMap.getList().map((item) => item.id));
      this._faceExtraDataMap.forEach((_, id) => {
        if (!baseIds.has(id)) this._faceExtraDataMap.delete(id);
      });
      baseIds.forEach((id) => this._syncExtraData(id));
      return;
    }

    const baseData = this._faceBaseDataMap.get(changedId);
    if (!baseData) {
      this._faceExtraDataMap.delete(changedId);
      return;
    }
    if (this._faceExtraDataMap.has(changedId)) return;

    this._faceExtraDataMap.set(changedId, {
      holes: [],
      baseData: createDefaultBaseData(baseData.baseDataType)
    });
  }

  add(data: IFaceData) {
    this._faceBaseDataMap.add({
      id: data.id,
      type: data.type,
      baseDataType: data.baseDataType,
      relativeInfo: data.relativeInfo,
      materialInfo: data.materialInfo
    });
    this._faceExtraDataMap.set(data.id, { holes: data.holes, baseData: data.baseData });
  }

  update(_id: string, _data: IFaceData) {
    console.warn('can not update face data directly');
  }

  updateMaterial(id: string, materialInfo: IMaterialInfo) {
    if (!id || !materialInfo) return;
    const currentData = this._faceBaseDataMap.get(id);
    if (!currentData) return;
    this._faceBaseDataMap.update(id, { materialInfo });
  }

  updateExtra(id: string, data: IFaceDataExtra) {
    if (!id || !data) return;
    const currentData = this._faceExtraDataMap.get(id);
    if (!currentData) {
      const d = this._faceBaseDataMap.get(id);
      if (!d) return;
      this._faceExtraDataMap.set(id, { ...data });
    } else {
      this._faceExtraDataMap.set(id, { ...data });
    }
  }

  remove(id: string) {
    if (!id || !this._faceBaseDataMap.get(id)) return;

    this._faceBaseDataMap.remove(id);
    this._faceExtraDataMap.delete(id);
  }

  get(id: string) {
    const baseData = this._faceBaseDataMap.get(id);
    if (!baseData) return;
    // _syncExtraData 保证 base 在则 extra 在，取不到属于程序错误，按取不到处理不再往下拼半份数据
    const extraData = this._faceExtraDataMap.get(id);
    if (!extraData) return;

    return {
      ...baseData,
      ...extraData
    };
  }

  getList() {
    return this._faceBaseDataMap.getList().flatMap((item) => {
      const extraData = this._faceExtraDataMap.get(item.id);
      return extraData ? [{ ...item, ...extraData }] : [];
    });
  }

  clear() {
    this._faceBaseDataMap.clear();
    this._faceExtraDataMap.clear();
  }
}

function createDefaultBaseData(baseDataType: EFaceDataBaseType): IFaceDataExtra['baseData'] {
  switch (baseDataType) {
    case EFaceDataBaseType.Edge:
      return { start: { x: 0, y: 0 }, end: { x: 0, y: 0 }, height: 0, offGround: 0 };
    case EFaceDataBaseType.ArcEdge:
      return {
        start: { x: 0, y: 0 },
        end: { x: 0, y: 0 },
        height: 0,
        offGround: 0,
        center: { x: 0, y: 0 },
        clockwise: false
      };
    case EFaceDataBaseType.Contour:
      return { contour: [], offGround: 0 };
    case EFaceDataBaseType.Hole:
      return { center: { x: 0, y: 0 }, width: 0, height: 0, thickness: 0, offGround: 0, rotation: 0 };
    case EFaceDataBaseType.Cylinder:
      return { center: { x: 0, y: 0 }, radius: 0, height: 0, offGround: 0 };
  }
}
